import { createStyles, Divider, Group, Stack } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useInputState } from '@mantine/hooks';
import type { ApiKeyOut, PagedApiKeyOut } from '@repo/api-codegen';
import { useApiCreateApiKey, useApiUpdateApiKey } from '@repo/api-codegen';
import type { ApiKeyOutV2 } from '@repo/api-codegen/v2';
import { apiListApiKeysV2QueryKey } from '@repo/api-codegen/v2';
import { Button, CopyButton, TextInput } from '@repo/foundations';
import { useCallback, useState } from 'react';
import { queryClient } from '../../../api';
import { closeAllModals } from '../../ModalManager';

interface ApiKeyModalProps {
	apiKey?: ApiKeyOutV2;
}

const useStyles = createStyles((theme) => ({
	disabledTextInputWrapper: {
		'&:has(input:disabled) .mantine-TextInput-rightSection': {
			display: 'flex',
		},
	},
	disabledTextInputInput: {
		'&:disabled': {
			color: `${theme.other.getColor('text/primary/default')} !important`,
			opacity: 'unset',
		},
	},
	content: {
		padding: theme.spacing.md,
	},
	footer: {
		padding: theme.spacing.md,
		justifyContent: 'flex-end',
		gap: theme.spacing.xs,
	},
}));

export function ApiKeyModal({ apiKey }: ApiKeyModalProps) {
	const [key, setKey] = useState<ApiKeyOut>();
	const [description, setDescription] = useInputState(
		apiKey?.description ?? ''
	);
	const [expiresAt, setExpiresAt] = useState<Date | null>(
		apiKey?.expires_at ? new Date(apiKey.expires_at) : null
	);

	const { classes } = useStyles();

	const { mutateAsync: createKey, isPending: isCreateKeyLoading } =
		useApiCreateApiKey({
			onSuccess: (newApiKey) => {
				queryClient.setQueryData<PagedApiKeyOut>(
					apiListApiKeysV2QueryKey({}),
					(prev) => {
						if (!prev) {
							return prev;
						}

						return {
							...prev,
							results: [...prev.results, newApiKey],
						};
					}
				);
			},
		});

	const { mutateAsync: updateKey, isPending: isUpdateKeyLoading } =
		useApiUpdateApiKey({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: apiListApiKeysV2QueryKey({}),
				});
			},
		});

	const handleCreateKey = useCallback(async () => {
		const newKey = await createKey({
			body: {
				description,
				expires_at: expiresAt?.toISOString(),
			},
		});
		setKey(newKey);
	}, [description, expiresAt, createKey]);

	const handleUpdateKey = useCallback(async () => {
		if (!apiKey) return;

		await updateKey({
			body: {
				description,
				expires_at: expiresAt?.toISOString(),
			},
			pathParams: {
				apiKeyId: apiKey.id,
			},
		});
		closeAllModals();
	}, [apiKey, description, expiresAt, updateKey]);

	const handleClose = useCallback(() => {
		closeAllModals();
	}, []);

	const isEditing = !!apiKey;
	const isLoading = isCreateKeyLoading || isUpdateKeyLoading;

	return (
		<Stack spacing="md">
			{key ? (
				<TextInput
					classNames={{
						wrapper: classes.disabledTextInputWrapper,
						input: classes.disabledTextInputInput,
					}}
					label="API key"
					value={key.key}
					help={
						'For security reasons, you will not be able to view this key again after closing this modal.'
					}
					rightSection={<CopyButton value={key.key} />}
					disabled
				/>
			) : (
				<>
					<TextInput
						{...(!isEditing && { 'data-autofocus': true })}
						label={'Name'}
						value={description}
						onChange={setDescription}
					/>

					<DatePickerInput
						popoverProps={{
							withinPortal: true,
						}}
						size="sm"
						label="Expiry date"
						placeholder="Select expiry date (optional)"
						value={expiresAt}
						onChange={setExpiresAt}
						clearable
						minDate={new Date()}
					/>
				</>
			)}
			<Divider />
			<Group align="center" position="right" spacing="xs">
				{!key && (
					<>
						<Button variant="default" onClick={handleClose}>
							Cancel
						</Button>
						<Button
							variant="primary"
							disabled={description === ''}
							loading={isLoading}
							onClick={isEditing ? handleUpdateKey : handleCreateKey}
						>
							{isEditing ? 'Save' : 'Create'}
						</Button>
					</>
				)}
				{key && (
					<Button variant="primary" onClick={handleClose}>
						Done
					</Button>
				)}
			</Group>
		</Stack>
	);
}
